import { useId, useState } from "react"

import {
  KnowledgeBaseDisableControl,
  LLMModelSelect,
  QuestionTextarea,
  TargetFilterToggle,
  TranscriptFilterDetails,
  TranscriptFilterToggle,
} from "@/metrics/registry/configuration"
import {
  supportsModelSelection,
  supportsTranscriptFilter,
} from "@/metrics/runtime"
import type { Annotation } from "@/types"
import { Button, Input } from "@coval/ui"
import { Trash2 } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import {
  getMetricAnswerExplanation,
  getMetricExecutionConfigDiff,
  getMetricExplanation,
  isMetricExecutionConfigCurrent,
  type MetricOutputBoundarySource,
} from "@/utils/metric-output-boundaries"

import { CategoricalMetricRecord } from "../base/CategoricalMetricRecord"
import { CategoricalHumanFeedbackButton } from "../helpers/CategoricalHumanFeedbackButton"
import { getMetricQuestionSection } from "../helpers/getMetricQuestionSection"

// Add new component for category input
const CategoryInput: React.FC<{
  categories: string[]
  onChange: (categories: string[]) => void
  isDisabled?: boolean
}> = ({ categories, onChange, isDisabled = false }) => {
  const inputId = useId()
  const [newCategory, setNewCategory] = useState("")
  const trimmedNewCategory = newCategory.trim()
  const categoryHasComma = trimmedNewCategory.includes(",")

  const handleAddCategory = () => {
    if (
      trimmedNewCategory &&
      !categoryHasComma &&
      !categories.some((category) => category.trim() === trimmedNewCategory)
    ) {
      onChange([...categories, trimmedNewCategory])
      setNewCategory("")
    }
  }

  const handleRemoveCategory = (category: string) => {
    onChange(categories.filter((c) => c !== category))
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <div className="flex flex-col gap-1 flex-1">
          <label htmlFor={inputId} className="text-sm font-medium">
            Add Category
          </label>
          <Input
            id={inputId}
            required
            value={newCategory}
            placeholder="Enter a category"
            onChange={(e) => setNewCategory(e.target.value)}
            disabled={isDisabled}
          />
        </div>
        <Button
          disabled={
            !trimmedNewCategory ||
            categoryHasComma ||
            isDisabled ||
            categories.some(
              (category) => category.trim() === trimmedNewCategory,
            )
          }
          onClick={handleAddCategory}
          className="self-end"
        >
          Add
        </Button>
      </div>
      {categoryHasComma && (
        <span className="text-sm text-coval-danger-text">
          Category labels cannot contain commas.
        </span>
      )}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <div
            key={category}
            className="flex items-center gap-1 px-2 py-1 bg-surface-1 rounded-md"
          >
            <span>{category}</span>
            <Button
              icon
              variant="ghost"
              onClick={() => handleRemoveCategory(category)}
              className="text-coval-text-muted hover:text-coval-danger-text"
              tooltip={`Remove ${category}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
      {categories.length < 2 && (
        <span className="text-sm text-coval-danger-text">
          Add at least two categories.
        </span>
      )}
    </div>
  )
}

export class CategoricalLLMMetricRecord extends CategoricalMetricRecord {
  overviewSize = "medium" as const

  get hasVersionedConfig() {
    return true
  }

  isMetricOutputCurrentVersion(
    metricOutput: MetricOutputBoundarySource,
  ): boolean {
    return isMetricExecutionConfigCurrent(
      this.metric_metadata?.question,
      metricOutput,
      this,
    )
  }

  getVersionDiff(
    metricOutput: MetricOutputBoundarySource,
  ): Array<{ field: string; current: string; previous: string }> {
    const diff = getMetricExecutionConfigDiff(
      this.metric_metadata?.question,
      metricOutput,
      this,
    )
    return diff ? [diff] : []
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const displayValue = mo.string_value ?? mo.set_value?.join(", ")
    const answer_explanation =
      getMetricAnswerExplanation(mo) ?? getMetricExplanation(mo)
    return (
      <div>
        <div>{displayValue ?? "--"}</div>
        {answer_explanation && (
          <div>
            <strong>Explanation</strong>: {answer_explanation}
          </div>
        )}
      </div>
    )
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    const showTranscriptFilter = supportsTranscriptFilter(metricRecordData)
    const showModelSelection = supportsModelSelection(metricRecordData)

    return (
      <div className="space-y-6">
        <QuestionTextarea
          metricRecordData={metricRecordData}
          onChange={onChange}
          label="Category Prompt"
          required
          placeholder="Enter a prompt that helps the LLM classify the conversation into one of your categories.

Example: 'What was the primary reason for this customer call?'

Add your categories below and the LLM will select the most appropriate one based on the conversation content."
        />

        <CategoryInput
          categories={
            metricRecordData.metric_metadata.additional_information?.value
              ?.split(",")
              .filter(Boolean) || []
          }
          onChange={(categories) =>
            onChange({
              ...metricRecordData,
              metric_metadata: {
                ...metricRecordData.metric_metadata,
                additional_information: {
                  key: "categories",
                  value: categories.join(","),
                },
              },
            })
          }
        />

        <div className="grid grid-cols-3 gap-6">
          <KnowledgeBaseDisableControl
            metricRecordData={metricRecordData}
            onChange={onChange}
          />
          {showModelSelection && (
            <LLMModelSelect
              metricRecordData={metricRecordData}
              onChange={onChange}
            />
          )}
          {showTranscriptFilter && (
            <TranscriptFilterToggle
              metricRecordData={metricRecordData}
              onChange={onChange}
            />
          )}
        </div>
        {showTranscriptFilter && (
          <TranscriptFilterDetails
            metricRecordData={metricRecordData}
            onChange={onChange}
          />
        )}
        <TargetFilterToggle
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
      </div>
    )
  }

  getDeepDiveSections(
    mo: RunMetricOutput,
  ): { label: string; content: React.ReactNode }[] | null {
    const sections: { label: string; content: React.ReactNode }[] = []

    const questionSection = getMetricQuestionSection(
      mo,
      this.metric_metadata?.question,
    )
    if (questionSection) {
      sections.push(questionSection)
    }

    const answerExplanation =
      getMetricAnswerExplanation(mo) ?? getMetricExplanation(mo)

    if (answerExplanation) {
      sections.push({
        label: "Explanation",
        content: (
          <div className="text-sm whitespace-pre-wrap wrap-break-word">
            {answerExplanation}
          </div>
        ),
      })
    }

    return sections.length > 0 ? sections : null
  }

  hasCustomVotingUI = true

  protected getHumanFeedbackCategoricalOptions(): string[] {
    return (
      this.metric_metadata?.additional_information?.value
        ?.split(",")
        .filter(Boolean) || []
    )
  }

  renderHumanFeedbackValueButton(
    annotation: Annotation | null,
    onValueChange: (
      floatValue: number | null,
      stringValue: string | null,
      setValue: string[] | null,
    ) => void,
    onExplanationChange: (explanation: string | null) => void,
    otherUserAnnotations?: Annotation[],
  ): React.ReactNode {
    let categories = this.getHumanFeedbackCategoricalOptions()

    if (this.metric_name === "metric_type_end_call_reason") {
      categories = [
        "Call Transfer",
        "Customer Ended",
        "Silence Time Out",
        "Other",
      ]
    }

    return (
      <CategoricalHumanFeedbackButton
        annotation={annotation}
        onValueChange={onValueChange}
        onExplanationChange={onExplanationChange}
        otherUserAnnotations={otherUserAnnotations}
        categories={categories}
      />
    )
  }
}
